/**
 * Overlay State Hook
 *
 * Manages active overlays and sub-filters for visitor map.
 * State is session-only (not persisted across page loads).
 */

import { useState, useCallback, useMemo } from "react";
import type { OverlayDefinition } from "@wondermaps/maps";
import { getOverlayById } from "@wondermaps/maps";

export interface OverlayState {
  /** IDs of currently active overlays */
  activeOverlayIds: string[];
  /** Sub-filter selections per overlay (e.g., dietary: ["vegan", "gluten_free"]) */
  subFilters: Record<string, string[]>;
}

export interface UseOverlayStateResult {
  /** Currently active overlay IDs */
  activeOverlayIds: string[];
  /** Sub-filters per overlay */
  subFilters: Record<string, string[]>;
  /** Toggle an overlay on/off */
  toggleOverlay: (overlayId: string) => void;
  /** Toggle a sub-filter within an overlay */
  toggleSubFilter: (overlayId: string, subFilterSlug: string) => void;
  /** Clear all active overlays */
  clearAllOverlays: () => void;
  /** Check if an overlay is active */
  isOverlayActive: (overlayId: string) => boolean;
  /** Check if a sub-filter is active */
  isSubFilterActive: (overlayId: string, subFilterSlug: string) => boolean;
  /** Get active overlays as definitions */
  activeOverlays: OverlayDefinition[];
  /** Whether any overlay is currently active */
  hasActiveOverlays: boolean;
}

/**
 * Parse overlay state from URL hash fragment
 * Format: #overlay=wheelchair,dietary&dietary=vegan,gluten_free
 */
function parseHashState(): OverlayState {
  if (typeof window === "undefined") {
    return { activeOverlayIds: [], subFilters: {} };
  }

  const hash = window.location.hash.slice(1); // Remove #
  if (!hash) {
    return { activeOverlayIds: [], subFilters: {} };
  }

  const params = new URLSearchParams(hash);
  const overlayParam = params.get("overlay");
  const activeOverlayIds = overlayParam
    ? overlayParam.split(",").filter((id) => getOverlayById(id))
    : [];

  const subFilters: Record<string, string[]> = {};
  for (const overlayId of activeOverlayIds) {
    const subParam = params.get(overlayId);
    if (subParam) {
      subFilters[overlayId] = subParam.split(",");
    }
  }

  return { activeOverlayIds, subFilters };
}

/**
 * Serialize overlay state to URL hash fragment
 */
function serializeToHash(state: OverlayState): string {
  if (state.activeOverlayIds.length === 0) {
    return "";
  }

  const params = new URLSearchParams();
  params.set("overlay", state.activeOverlayIds.join(","));

  for (const [overlayId, filters] of Object.entries(state.subFilters)) {
    if (filters.length > 0 && state.activeOverlayIds.includes(overlayId)) {
      params.set(overlayId, filters.join(","));
    }
  }

  return `#${params.toString()}`;
}

/**
 * Manage active overlays and sub-filters for visitor accessibility filtering.
 *
 * @param syncToUrl - If true, sync state to URL hash fragment
 */
export function useOverlayState(syncToUrl = false): UseOverlayStateResult {
  const [state, setState] = useState<OverlayState>(() =>
    syncToUrl ? parseHashState() : { activeOverlayIds: [], subFilters: {} }
  );

  const toggleOverlay = useCallback(
    (overlayId: string) => {
      setState((prev) => {
        const isActive = prev.activeOverlayIds.includes(overlayId);
        let newState: OverlayState;
        if (isActive) {
          // Remove overlay and its sub-filters
          const { [overlayId]: _, ...restSubFilters } = prev.subFilters;
          newState = {
            activeOverlayIds: prev.activeOverlayIds.filter((id) => id !== overlayId),
            subFilters: restSubFilters,
          };
        } else {
          // Add overlay
          newState = {
            ...prev,
            activeOverlayIds: [...prev.activeOverlayIds, overlayId],
          };
        }
        // Side effect: sync to URL if enabled
        if (syncToUrl) {
          const hash = serializeToHash(newState);
          if (hash) {
            window.history.replaceState(null, "", hash);
          } else {
            window.history.replaceState(
              null,
              "",
              window.location.pathname + window.location.search
            );
          }
        }
        return newState;
      });
    },
    [syncToUrl]
  );

  const toggleSubFilter = useCallback(
    (overlayId: string, subFilterSlug: string) => {
      setState((prev) => {
        const currentFilters = prev.subFilters[overlayId] ?? [];
        const isActive = currentFilters.includes(subFilterSlug);

        let newFilters: string[];
        if (isActive) {
          newFilters = currentFilters.filter((f) => f !== subFilterSlug);
        } else {
          newFilters = [...currentFilters, subFilterSlug];
        }

        // Auto-enable overlay if a sub-filter is selected
        let newActiveIds = prev.activeOverlayIds;
        if (!prev.activeOverlayIds.includes(overlayId) && newFilters.length > 0) {
          newActiveIds = [...prev.activeOverlayIds, overlayId];
        }

        const newState = {
          activeOverlayIds: newActiveIds,
          subFilters: {
            ...prev.subFilters,
            [overlayId]: newFilters,
          },
        };

        // Side effect: sync to URL if enabled
        if (syncToUrl) {
          const hash = serializeToHash(newState);
          if (hash) {
            window.history.replaceState(null, "", hash);
          } else {
            window.history.replaceState(
              null,
              "",
              window.location.pathname + window.location.search
            );
          }
        }

        return newState;
      });
    },
    [syncToUrl]
  );

  const clearAllOverlays = useCallback(() => {
    const newState: OverlayState = { activeOverlayIds: [], subFilters: {} };
    setState(newState);
    if (syncToUrl) {
      // Remove hash without triggering scroll
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search
      );
    }
  }, [syncToUrl]);

  const isOverlayActive = useCallback(
    (overlayId: string) => state.activeOverlayIds.includes(overlayId),
    [state.activeOverlayIds]
  );

  const isSubFilterActive = useCallback(
    (overlayId: string, subFilterSlug: string) =>
      state.subFilters[overlayId]?.includes(subFilterSlug) ?? false,
    [state.subFilters]
  );

  const activeOverlays = useMemo(
    () =>
      state.activeOverlayIds
        .map((id) => getOverlayById(id))
        .filter((o): o is OverlayDefinition => o !== undefined),
    [state.activeOverlayIds]
  );

  const hasActiveOverlays = state.activeOverlayIds.length > 0;

  return {
    activeOverlayIds: state.activeOverlayIds,
    subFilters: state.subFilters,
    toggleOverlay,
    toggleSubFilter,
    clearAllOverlays,
    isOverlayActive,
    isSubFilterActive,
    activeOverlays,
    hasActiveOverlays,
  };
}
